/**
 * java.awt.event shims. The game's listeners keep their AWT signatures
 * (mousePressed(MouseEvent), keyPressed(KeyEvent), …); Game translates DOM events into these.
 *
 * Coordinates are canvas-relative device pixels, exactly like AWT's component-relative
 * coordinates — the game converts to logical space itself via Game.toGameX/toGameY.
 */

export class MouseEvent {
  static readonly BUTTON1 = 1; // left
  static readonly BUTTON2 = 2; // middle
  static readonly BUTTON3 = 3; // right

  constructor(
    private readonly x: number,
    private readonly y: number,
    private readonly button: number,
  ) {}

  getX(): number { return this.x; }
  getY(): number { return this.y; }
  getButton(): number { return this.button; }
}

export class MouseWheelEvent extends MouseEvent {
  constructor(x: number, y: number, private readonly rotation: number) {
    super(x, y, 0);
  }

  /** Negative = wheel up / away from user, positive = down. Same sign convention as AWT. */
  getWheelRotation(): number { return this.rotation; }
}

export class KeyEvent {
  // Only the codes the game actually reads.
  static readonly VK_A = 65;
  static readonly VK_D = 68;
  static readonly VK_E = 69;
  static readonly VK_F = 70;
  static readonly VK_P = 80;
  static readonly VK_Q = 81;
  static readonly VK_S = 83;
  static readonly VK_W = 87;
  static readonly VK_UP = 38;
  static readonly VK_DOWN = 40;
  static readonly VK_LEFT = 37;
  static readonly VK_RIGHT = 39;
  static readonly VK_SHIFT = 16;
  static readonly VK_SPACE = 32;
  static readonly VK_ESCAPE = 27;
  static readonly VK_ENTER = 10;

  constructor(private readonly keyCode: number) {}

  getKeyCode(): number { return this.keyCode; }
}

/**
 * Map a DOM KeyboardEvent to an AWT virtual key code.
 *
 * Keyed off `event.code` (physical key) so WASD keeps working on AZERTY/Dvorak layouts,
 * which `event.key` would not. Returns -1 for keys the game ignores.
 */
export function domKeyToVK(e: KeyboardEvent): number {
  switch (e.code) {
    case 'KeyW': return KeyEvent.VK_W;
    case 'KeyA': return KeyEvent.VK_A;
    case 'KeyS': return KeyEvent.VK_S;
    case 'KeyD': return KeyEvent.VK_D;
    case 'KeyE': return KeyEvent.VK_E;
    case 'KeyF': return KeyEvent.VK_F;
    case 'KeyP': return KeyEvent.VK_P;
    case 'KeyQ': return KeyEvent.VK_Q;
    case 'ArrowUp': return KeyEvent.VK_UP;
    case 'ArrowDown': return KeyEvent.VK_DOWN;
    case 'ArrowLeft': return KeyEvent.VK_LEFT;
    case 'ArrowRight': return KeyEvent.VK_RIGHT;
    case 'ShiftLeft':
    case 'ShiftRight': return KeyEvent.VK_SHIFT;
    case 'Space': return KeyEvent.VK_SPACE;
    case 'Escape': return KeyEvent.VK_ESCAPE;
    case 'Enter':
    case 'NumpadEnter': return KeyEvent.VK_ENTER;
    default: return -1;
  }
}
